import type { ContribuinteDetalhado } from '@/services/Contribuintes/types';

export type EmpresaDropdownItem =
  | {
      kind: 'single';
      orderIndex: number;
      empresa: ContribuinteDetalhado;
    }
  | {
      kind: 'group';
      orderIndex: number;
      groupKey: string;
      matriz: ContribuinteDetalhado;
      vinculadas: ContribuinteDetalhado[];
    };

export function parsePrefixNumber(nome?: string | null): number {
  const match = (nome || '').match(/^(\d+)/);
  return match ? Number.parseInt(match[1], 10) : Number.POSITIVE_INFINITY;
}

export function getCnpjRaiz(cnpj?: string | null): string {
  return (cnpj || '').replace(/\D/g, '').slice(0, 8);
}

export function getCnpjFilial(cnpj?: string | null): string {
  return (cnpj || '').replace(/\D/g, '').slice(8, 12);
}

export function matchesEmpresaSearch(
  contribuinte: ContribuinteDetalhado,
  rawSearch: string
): boolean {
  const search = rawSearch.toLowerCase().trim();
  if (!search) return true;

  const nome = (contribuinte.Nome || '').toLowerCase();
  const cnpjDigits = (contribuinte.CNPJ_Contribuinte || '').replace(/\D/g, '');
  const searchDigits = search.replace(/\D/g, '');

  const matchNome = nome.includes(search);
  const matchCnpj = !!searchDigits && cnpjDigits.includes(searchDigits);

  return matchNome || matchCnpj;
}

export function sortContribuintesForDropdown(
  contribuintes: ContribuinteDetalhado[] | undefined
): ContribuinteDetalhado[] {
  if (!contribuintes) return [];

  return [...contribuintes].sort((a, b) => {
    const numA = parsePrefixNumber(a.Nome);
    const numB = parsePrefixNumber(b.Nome);
    if (numA !== numB) return numA - numB;
    return (a.Nome || '').localeCompare(b.Nome || '', 'pt-BR');
  });
}

export function buildEmpresaDropdownItems(
  contribuintesOrdenados: ContribuinteDetalhado[]
): EmpresaDropdownItem[] {
  const byRaiz = new Map<string, ContribuinteDetalhado[]>();
  const raizOrderIndex = new Map<string, number>();

  contribuintesOrdenados.forEach((empresa, index) => {
    const raiz = getCnpjRaiz(empresa.CNPJ_Contribuinte);
    if (!raiz) return;

    const current = byRaiz.get(raiz) ?? [];
    current.push(empresa);
    byRaiz.set(raiz, current);

    const existing = raizOrderIndex.get(raiz);
    if (existing === undefined || index < existing) {
      raizOrderIndex.set(raiz, index);
    }
  });

  const items: EmpresaDropdownItem[] = [];

  for (const [raiz, empresas] of byRaiz.entries()) {
    const orderIndex = raizOrderIndex.get(raiz) ?? 0;

    if (empresas.length === 1) {
      items.push({ kind: 'single', orderIndex, empresa: empresas[0] });
      continue;
    }

    const ordenadas = [...empresas].sort((a, b) => {
      const pa = parsePrefixNumber(a.Nome);
      const pb = parsePrefixNumber(b.Nome);
      if (pa !== pb) return pa - pb;
      return (a.Nome || '').localeCompare(b.Nome || '', 'pt-BR');
    });

    const matrizIndex = ordenadas.findIndex(
      empresa => getCnpjFilial(empresa.CNPJ_Contribuinte) === '0001'
    );
    const matriz = matrizIndex >= 0 ? ordenadas[matrizIndex] : ordenadas[0];
    const vinculadas = ordenadas.filter(empresa => empresa !== matriz);

    items.push({ kind: 'group', orderIndex, groupKey: raiz, matriz, vinculadas });
  }

  return items.sort((a, b) => a.orderIndex - b.orderIndex);
}
